import { H5 } from "@blueprintjs/core";
import type { ReactElement } from "react";

interface NavbarSectionProps {
  /** Icon element shown in the clickable "tab" cell. */
  icon: ReactElement;
  /** Accessible label for the icon tab. */
  iconAriaLabel: string;
  /** Click handler for the icon tab — typically navigates to section/app home. */
  onIconClick?: () => void;
  /** Section heading (module name or app name). Rendered as a Blueprint <H5>. */
  title: string;
}

/**
 * Navbar section — Foundry-style icon-tab + heading grouping that lives
 * inside an <AppHeader>. The icon cell is a clickable, focusable "tab"
 * (div with tabindex, not a button — Foundry's pattern, lets the icon
 * cell hold richer content if needed without button-in-button issues).
 * The heading uses Blueprint's <H5> for consistent typography.
 */
export function NavbarSection({ icon, iconAriaLabel, onIconClick, title }: NavbarSectionProps) {
  return (
    <div data-testid="navbar-section" className="bp-navbar-section">
      <div
        className="bp-navbar-section-tab"
        role={onIconClick ? "button" : undefined}
        tabIndex={onIconClick ? 0 : undefined}
        aria-label={iconAriaLabel}
        onClick={onIconClick}
        onKeyDown={
          onIconClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onIconClick();
                }
              }
            : undefined
        }
      >
        {icon}
      </div>
      <H5 className="bp-navbar-section-title">{title}</H5>
    </div>
  );
}
